/* -----------------Icons--------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { isI18nKeyExists } from "@helpers/common";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import type { Confidence } from "@memorang/types/src/session/common";
import type {
	ItemCriteria,
	OriginalItemTypes,
	OriginalStimulusTypes,
	RenderableItemTypes,
	SubskillAssessment,
} from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import CustomChip from "@memorang/ui/src/components/CustomChip";
import DonutChart from "@memorang/ui/src/components/DonutChart";
import { OverlineText } from "@memorang/ui/src/components/OverlineText";
import type { IconType } from "@memorang/ui/src/types";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable } from "react-native";
import { Text } from "react-native-paper";
import { getNormalizedScore } from "../../helpers/ai-feedback";

type Props = {
	confidence?: Confidence;
	numAnsweredStat?: string;
	correct?: boolean;
	pointsEarnedStatValue?: string;
	score?: number;
	subskillAssessments?: SubskillAssessment[];
	totalScore: number;
	itemType?: RenderableItemTypes;
	originalItemType?: OriginalItemTypes;
	stimulusType?: OriginalStimulusTypes;
	itemCriteria?: ItemCriteria[];
};

const getSubskillLabels = (
	itemType: RenderableItemTypes | undefined,
): ItemCriteria[] => {
	if (itemType === "AUDIO_INPUT") {
		return [
			{
				name: "session.subskills.delivery",
				icon: "microphone-outline",
			},
			{ name: "session.subskills.languageUse", icon: "translate" },
			{
				name: "session.subskills.topicDevelopment",
				icon: "comment-text-outline",
			},
		];
	}

	return [
		{
			name: "session.subskills.content",
			icon: "file-document-outline",
		},
		{ name: "session.subskills.languageUse", icon: "translate" },
		{
			name: "session.subskills.organizationAndDevelopment",
			icon: "text-box-outline",
		},
	];
};

const getSubskillDescriptionLabel = (
	originalItemType: OriginalItemTypes | undefined,
	stimulusType: OriginalStimulusTypes | undefined,
) => {
	if (stimulusType === "INDEPENDENT_SPEAKING_STIMULUS") {
		return "independentSpeaking";
	}
	if (originalItemType === "AUDIO_INPUT") {
		return "integratedSpeaking";
	}
	if (originalItemType === "ACADEMIC_DISCUSSION_RESPONSE") {
		return "academicDiscussionWriting";
	}
	if (originalItemType === "INTEGRATED_WRITING_RESPONSE") {
		return "integratedWriting";
	}

	return null;
};

const ScoreItem = ({
	label,
	score,
	icon,
	backgroundColor,
	color,
	description,
}: {
	label: string;
	score: number | string;
	icon: IconType;
	backgroundColor: string;
	color: string;
	description: string | null;
}) => {
	const theme = useAppTheme();
	const [showInfoDialog, setShowInfoDialog] = useState(false);

	const openInfoDialog = () => {
		setShowInfoDialog(true);
	};

	return (
		<>
			<Pressable
				style={{
					flexDirection: "row",
					justifyContent: "space-between",
					alignItems: "center",
				}}
				onPress={openInfoDialog}
			>
				<Box flexDirection="row" alignItems="center" gap={16}>
					<Icon name={icon} size={18} color={theme.colors.text} />
					<Text variant="bodyLarge">{label}</Text>
				</Box>
				<CustomChip
					style={{ backgroundColor }}
					textStyle={{ color }}
					onPress={openInfoDialog}
				>
					{score}
				</CustomChip>
			</Pressable>
			<ConfirmDialog
				dismissCallback={() => setShowInfoDialog(false)}
				body={description ?? ""}
				icon="information-outline"
				title={label}
				visible={showInfoDialog}
				confirmCta="Close"
				confirmCallback={() => setShowInfoDialog(false)}
			/>
		</>
	);
};

const AiPostAnswerHeader = (props: Props) => {
	const {
		score,
		subskillAssessments,
		totalScore,
		itemType,
		originalItemType,
		stimulusType,
		itemCriteria,
	} = props;
	const theme = useAppTheme();
	const { t } = useTranslation();
	const masteryLevels = useAppStore((state) => state.app.masteryLevels!);

	const getChipColor = useCallback(
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
		(score: number | undefined) => {
			const { none: empty } = theme.colors.progress;
			if (score === undefined) {
				return { backgroundColor: empty, color: theme.colors.text };
			}
			const normalizedScore = getNormalizedScore(score, totalScore);

			if (normalizedScore === undefined) {
				return { backgroundColor: empty, color: theme.colors.text };
			}
			if (normalizedScore < 40) {
				const lowBucket = masteryLevels.find(
					(levels) => levels.level === "low",
				);
				return {
					backgroundColor: theme.dark
						? lowBucket?.darkColor
						: lowBucket?.lightColor,
					color: "white",
				};
			}
			if (normalizedScore < 60) {
				const mediumBucket = masteryLevels.find(
					(levels) => levels.level === "medium",
				);

				return {
					backgroundColor: theme.dark
						? mediumBucket?.darkColor
						: mediumBucket?.lightColor,
					color: "white",
				};
			}
			const highBucket = masteryLevels.find(
				(levels) => levels.level === "high",
			);
			if (normalizedScore < 80) {
				return {
					backgroundColor: theme.dark
						? highBucket?.darkColor
						: highBucket?.lightColor,
					color: "white",
				};
			}
			return {
				backgroundColor: theme.dark
					? highBucket?.darkColor
					: highBucket?.lightColor,
				color: "white",
			};
		},
		[
			theme.colors.progress,
			totalScore,
			theme.colors.text,
			theme.dark,
			masteryLevels,
		],
	);

	const subskillsStats = useMemo(() => {
		const subskills = itemCriteria ?? getSubskillLabels(itemType);
		const subskillScores = new Map(
			subskillAssessments?.map((subskill) => [
				subskill.name?.toLowerCase(),
				subskill.score,
			]),
		);

		return subskills.map(
			// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
			({ name, icon, description: subskillDescription, maxPoints }) => {
				const title = `${name}.title`;
				const titleText = isI18nKeyExists(title)
					? t(title, { lng: "en-US" }).toLowerCase()
					: name.toLowerCase();
				const subskillScore = subskillScores.get(titleText);
				const label = isI18nKeyExists(title) ? t(title) : name;
				const chipColor = getChipColor(subskillScore);
				const description =
					subskillDescription ??
					getSubskillDescriptionLabel(originalItemType, stimulusType);

				return (
					<ScoreItem
						key={label}
						label={label}
						score={`${subskillScore ?? "--"} / ${itemCriteria ? maxPoints : totalScore}`}
						icon={icon as IconType}
						backgroundColor={chipColor.backgroundColor}
						color={chipColor.color}
						description={
							description
								? isI18nKeyExists(`session.subskills.content.${description}`)
									? t(`session.subskills.content.${description}`)
									: description
								: null
						}
					/>
				);
			},
		);
	}, [
		itemType,
		subskillAssessments,
		t,
		getChipColor,
		originalItemType,
		stimulusType,
		totalScore,
		itemCriteria,
	]);

	return (
		<Box gap={16} marginTop={16}>
			<Box alignItems="center" justifyContent="center">
				<DonutChart
					skeleton={score === undefined}
					distribution={[score ?? 0, totalScore - (score ?? 0)]}
					innerRadius={52}
					variant="progress"
					radius={64}
					score={score ? (score / totalScore) * 100 : undefined}
				>
					<Box alignItems="center" justifyContent="center">
						<Text variant="titleLarge">
							{score === undefined
								? `-- / ${totalScore}`
								: `${score} / ${totalScore}`}
						</Text>
						<OverlineText>{t("session.overall")}</OverlineText>
					</Box>
				</DonutChart>
			</Box>
			{subskillsStats}
		</Box>
	);
};

export default AiPostAnswerHeader;
